import React, { useState } from "react";
import {
  Center,
  Flex,
  HStack,
  Table,
  Text,

  ProgressCircle,

  ButtonGroup,
  IconButton,
  Pagination,
  ColorSwatch,
  NativeSelect,
  Card,
  Portal, 
  Select, 
  createListCollection

} from "@chakra-ui/react";
// import { Select } from "@chakra-ui/react/select";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import {
  IEmailAIExtractedData,
  IEmail,
  IApiLogs,
  IAgent,
  IEmailAIExtractedOwner,
} from "@/types/common";
import { API_COLORS, REVIEW_COLORS, REVIEW_STATUS } from "@/constants/common";
import DownloadFilesButton from "./components/DownloadFilesButton";
import ViewContentDialogButton from "./components/ViewContentDialogButton";
import ViewErrorDialogButton from "./components/ViewErrorDialogButton";
import NotesDialogButton from "./components/NotesDialogButton";
import ViewResponseAndRequestDialogButton from "@/components/tables/components/ViewResponseAndRequestDialogButton";
import ResolveManuallyButton from "./components/ResolveManuallyButton";
import MoveToManualReviewButton from "./components/MoveToManualReviewButton";
import EmailAiExtractedDataDialog from "./components/email-ai-extracted-data/EmailAiExtractedDataDialog";
import OwnerBadges from "./components/OwnerBadges";
import { formatDateString } from "@/utils/date";

import BaseProgressCircle from "@/components/BaseProgressCircle";

import useTableSort from "./hooks/useTableSort";


// @ts-ignore 
export interface FieldData extends IEmailAIExtractedData, IEmail, IAgent, IApiLogs {}

interface BaseTableProps {
  data: Partial<FieldData>[];
  columns: BaseTableColumn[];
  tableColumns?: typeof TABLE_COLUMNS;
  totalCount?: number;
  pagination?: { page: number; limit: number; };
  setPagination?: (pagination: { page: number; limit: number ;}) => void;
  loading?: boolean;

  clientPaginate?: boolean;
}

const BaseTable = ({
  data,
  columns,
  tableColumns = TABLE_COLUMNS,
  totalCount = 0,
  pagination = { page: 1, limit: 10 },
  setPagination,
  loading,
  clientPaginate = false,

}: BaseTableProps) => {

  const { sortData, handleSort, sortField, sortDirection } = useTableSort();

  const noData = !data || data.length === 0;

  const sortedRows = sortData(data) ?? [];
  const rowsToRender =
    clientPaginate
      ? sortedRows.slice(
          (pagination.page - 1) * pagination.limit,
          pagination.page * pagination.limit,
        )
      : sortedRows;
  const paginationCount = clientPaginate ? (data?.length ?? 0) : totalCount;

  const handlePageChange = (nextPage: number) => {
    setPagination?.({...pagination, page: nextPage})
  };

  const handlelimitChange = (size: number) => {
    setPagination?.({ page: 1, limit: size })
  };

  return (
    <Flex direction="column" w="100%">
      <Table.Root size="sm" variant="outline" showColumnBorder minH={75}>
        <Table.Header>
          <Table.Row>
            {columns.map((column: BaseTableColumn, i: number) => {
              const { columnName, sortable = false } = tableColumns[column];
              const isSorted = sortField === column;

              return (
                <Table.ColumnHeader
                  key={i}
                  onClick={() => sortable && handleSort(column)}
                  cursor={sortable ? "pointer" : "default"}
                >
                  <HStack position={"relative"}>
                    <Text>{columnName}</Text>
                    {sortable && isSorted && (
                      <Text fontSize="xs" position={"absolute"} right={2}>
                        {sortDirection === "asc" ? "▲" : "▼"}
                      </Text>
                    )}
                  </HStack>
                </Table.ColumnHeader>
              );
            })}
          </Table.Row>
        </Table.Header>

        <Table.Body position={"relative"}>
          {loading
            ? (
              <Center position="absolute" left="0" right="0" top="0" bottom="0">
                <BaseProgressCircle size="xs" colorPalette={"brand"} />
              </Center>
            )
            : noData
            ? (
              <Center position="absolute" left="0" right="0" top="0" bottom="0">
                <Text color="GrayText">No matching records found</Text>
              </Center>
            )
            :
            rowsToRender.map((item, i) => (
              <Table.Row
                key={
                  (item as FieldData).id != null
                    ? String((item as FieldData).id)
                    : i
                }
              >
                {columns.map((column: BaseTableColumn, j: number) => (
                  <Table.Cell key={j} maxW="30%">
                    {tableColumns[column].renderCell(item as FieldData)}
                  </Table.Cell>
                ))}
              </Table.Row>
          ))

          }
        </Table.Body>
      </Table.Root>


      {!noData && (
        <Flex justify="end" align="center" mt={4}>
          <Select.Root 
            collection={rowsPerPageOptions} 
            size="sm" 
            width="110px" 
            defaultValue={[pagination.limit]}
            onValueChange={(e: { value: number[] }) => {
              handlelimitChange(e.value[0]);
            }}
          >
            <Select.HiddenSelect />
            {/* <Select.Label>Select framework</Select.Label> */}
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder="Row per page" />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content>
                  {rowsPerPageOptions.items.map((option: { value: number; label: string; }) => (
                    <Select.Item item={option} key={option.value}>
                      {option.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>


          <Pagination.Root
            count={paginationCount}
            pageSize={pagination.limit}
            page={pagination.page}
            onPageChange={(e: any) => handlePageChange(e.page)}
          >
            <ButtonGroup variant="ghost" size="sm">
              <Pagination.PrevTrigger asChild>
                <IconButton aria-label="prev">
                  <HiChevronLeft />
                </IconButton>
              </Pagination.PrevTrigger>

              <Pagination.Items
                render={(pageObj) => (
                  <IconButton
                    key={pageObj.value}
                    variant={{ base: "ghost", _selected: "outline" }}
                  >
                    {pageObj.value}
                  </IconButton>
                )}
              />

              <Pagination.Ellipsis />
              <Pagination.PageText />

              <Pagination.NextTrigger asChild>
                <IconButton aria-label="next">
                <HiChevronRight />
                </IconButton>
              </Pagination.NextTrigger>
            </ButtonGroup>
          </Pagination.Root>
        </Flex>
      )}
    </Flex>
  );
};

const rowsPerPageOptions = createListCollection({
  items: [
    { label: "5 / page", value: 5 },
    { label: "10 / page", value: 10 },
    { label: "20 / page", value: 20},
    { label: "50 / page", value: 50 },
  ],
})

export const TABLE_COLUMNS = {
  businessName: {
    field: "businessName",
    columnName: "Business Name",
    sortable: true,
    renderCell: (field: FieldData) => field?.businessName,
  },
  dba: {
    field: "dba",
    columnName: "Business DBA",
    sortable: true,
    renderCell: (field: FieldData) => field?.dba,
  },
  // last4ssn: {
  //   field: "last4ssn",
  //   columnName: "last 4 ssn",
  //   sortable: true,
  //   renderCell: (field: FieldData) => field?.last4ssn,
  // },
  // fullName: {
  //   field: "fullName",
  //   columnName: "Client Name",
  //   sortable: true,
  //   renderCell: (field: FieldData) => field?.fullName,
  // },
  owners: {
    field: "owners",
    columnName: "Owners",
    renderCell: (field: FieldData) => <OwnerBadges owners={field.owners} />,
  },
  createdAt: {
    field: "createdAt",
    columnName: "Processing date",
    sortable: true,
    dateString: true,
    renderCell: (field: FieldData) =>
      field?.createdAt && formatDateString(field?.createdAt),
  },
  processedActions: {
    field: "processedActions",
    columnName: "Actions",
    renderCell: (field: IEmailAIExtractedData) => (
      <Flex gap={2}>
        <DownloadFilesButton processedFiles={field?.processedFiles} />
        <EmailAiExtractedDataDialog emailAiExtractedData={field} />
      </Flex>
    ),
  },
  status: {
    field: "status",
    columnName: "Status",
    sortable: true,
    renderCell: (field: FieldData) => (
      <HStack>
        <ColorSwatch
          value={
            REVIEW_COLORS?.[field?.reviewStatus] || (REVIEW_COLORS[2] as string)
          }
          borderRadius={"50%"}
          size="2xs"
        />
        <Text>{REVIEW_STATUS[field?.reviewStatus]}</Text>
      </HStack>
    ),
  },
  resolveManually: {
    field: "resolveManually",
    columnName: "Actions",
    renderCell: (field: FieldData) => (
      <ResolveManuallyButton reviewEmail={field} />
    ),
  },
  dateTime: {
    field: "createdAt",
    columnName: "Date time",
    sortable: true,
    dateString: true,
    renderCell: (field: FieldData) =>
      field?.createdAt &&
      new Date(field.createdAt).toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
  },

  mailbox: {
    field: "mailbox",
    columnName: "Mailbox",
    sortable: true,
    renderCell: (field: FieldData) => field?.mailbox?.email,
  },
  sender: {
    field: "sender",
    columnName: "Sender",
    sortable: true,
    renderCell: (field: FieldData) => field?.sender,
  },
  subject: {
    field: "subject",
    columnName: "Subject",
    sortable: true,
    renderCell: (field: FieldData) => field?.subject,
  },
  content: {
    field: "content",
    columnName: "Content",
    renderCell: (field: FieldData) =>
      field?.id && <ViewContentDialogButton field={field} />,
  },
  error: {
    field: "error",
    columnName: "Error",
    renderCell: (field: FieldData) =>
      field?.id && <ViewErrorDialogButton field={field} />,
  },
  moveToManualReview: {
    field: "id",
    columnName: "Manual review",
    renderCell: (field: FieldData) =>
      field?.id != null ? (
        <MoveToManualReviewButton emailId={field.id} />
      ) : null,
  },
  notes: {
    field: "notes",
    columnName: "Notes",
    renderCell: (field: FieldData) =>
      field?.notes && <NotesDialogButton field={field} />,
  },
  documentName: {
    field: "documentName",
    columnName: "Document name",
    renderCell: (field: FieldData) =>
      field?.documents
        ? <OwnerBadges owners={field?.documents?.map(doc => ({ fullName: doc?.filename ?? '' })) as unknown as IEmailAIExtractedOwner[]} />
        : "-",
  },
  request: {
    field: "request",
    columnName: "Request",
    renderCell: (field: FieldData) => (
      <ViewResponseAndRequestDialogButton field={field} isRequest={true} />
    ),
  },
  response: {
    field: "response",
    columnName: "Response",
    renderCell: (field: FieldData) => (
      <ViewResponseAndRequestDialogButton field={field} />
    ),
  },
  apiStatus: {
    field: "status",
    columnName: "Status",
    renderCell: (field: FieldData) => (
      <HStack>
        <ColorSwatch
          value={
            API_COLORS?.[field?.success ? 0 : 1] || (API_COLORS[1] as string)
          }
          borderRadius={"50%"}
          size="2xs"
        />
        <Text>{field?.success ? "Success" : "Failed"}</Text>
      </HStack>
    ),
  },
  fullname: {
    field: "fullname",
    columnName: "Full name",
    renderCell: (field: FieldData) => `${field?.firstName} ${field?.lastName}`,
  },
  company: {
    field: "company",
    columnName: "Company",
    renderCell: (field: FieldData) => field?.company,
  },
  email: {
    field: "email",
    columnName: "Email",
    renderCell: (field: FieldData) => field?.email,
  },
  phone: {
    field: "phone",
    columnName: "Phone",
    renderCell: (field: FieldData) => field?.phoneNumber,
  },
};

export type BaseTableColumn = keyof typeof TABLE_COLUMNS;

export default BaseTable;
